import { GraphQLClient } from "graphql-request";
import { TokenStore, getAuthToken } from "lib/utils/auth";
import { isHttpSecure } from "lib/utils/utils";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";

const host = process.env.REACT_APP_SINGLE_ARC_HOSTNAME || window.location.hostname;
const scheme = isHttpSecure() ? "https" : "http";

/**
 * Every arc_auth endpoint, and where it lives on arc_auth.
 *
 *   "auth"  ->  /auth/<name>   authentication
 *   "root"  ->  /<name>        everything else
 *
 * arc_auth groups by responsibility, and the gateway mirrors that exactly:
 *
 *   authentication   /auth/{login,refresh,logout,validate}
 *                    -> http://<gateway-origin>/api/auth/<name>
 *                    -> http://<host>:7070/auth/<name>
 *
 *   user management  /graphql  (not authentication, so not under /auth)
 *                    -> http://<gateway-origin>/api/graphql
 *                    -> http://<host>:7070/graphql
 *
 * Both map onto the gateway with a plain "strip /api" and no rewrites.
 *
 * The group is declared here rather than inferred, and ArcAuthEndpoint derives
 * from this record — so a new endpoint cannot be added without stating where it
 * lives. Inferring it (e.g. "everything except graphql is under /auth") would
 * silently route a future root endpoint like /health to /auth/health: a URL that
 * exists nowhere, failing at runtime instead of at compile time.
 *
 * Keep in sync with arc_gateway/Caddyfile and arc_auth/arc_auth/server.py.
 */
const ARC_AUTH_ENDPOINTS = {
  login: "auth",
  refresh: "auth",
  logout: "auth",
  validate: "auth",
  graphql: "root",
} as const;

type ArcAuthEndpoint = keyof typeof ARC_AUTH_ENDPOINTS;

const arcAuthUrl = (name: ArcAuthEndpoint): string => {
  const underAuth = ARC_AUTH_ENDPOINTS[name] === "auth";
  const gateway = gatewayServiceBaseUrl("arcAuth"); // ".../api/auth"
  if (gateway) {
    return underAuth
      ? `${gateway}/${name}`
      : `${gateway.replace(/\/auth$/, "")}/${name}`; // ".../api/graphql"
  }
  const base = `${scheme}://${host}:7070`;
  return underAuth ? `${base}/auth/${name}` : `${base}/${name}`;
};

const arcAuthClient = new GraphQLClient(arcAuthUrl("graphql"), {
  requestMiddleware: (request) => {
    const token = getAuthToken();
    return {
      ...request,
      headers: {
        ...request.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };
  },
});

export interface ArcUser {
  username: string;
  isDefault: boolean;
  createdAt: string;
}

const USER_FIELDS = `username isDefault createdAt`;

export async function fetchUsers(): Promise<ArcUser[]> {
  const data = await arcAuthClient.request<{ users: ArcUser[] }>(
    `query { users { ${USER_FIELDS} } }`
  );
  return data.users;
}

export async function addUser(username: string, password: string): Promise<ArcUser> {
  const data = await arcAuthClient.request<{ addUser: ArcUser }>(
    `mutation AddUser($username: String!, $password: String!) {
       addUser(username: $username, password: $password) { ${USER_FIELDS} }
     }`,
    { username, password }
  );
  return data.addUser;
}

export async function changePassword(
  username: string,
  newPassword: string,
  currentPassword?: string
): Promise<ArcUser> {
  const data = await arcAuthClient.request<{ changePassword: ArcUser }>(
    `mutation ChangePassword($username: String!, $newPassword: String!, $currentPassword: String) {
       changePassword(username: $username, newPassword: $newPassword, currentPassword: $currentPassword) { ${USER_FIELDS} }
     }`,
    { username, newPassword, currentPassword }
  );
  return data.changePassword;
}

export async function deleteUser(username: string): Promise<boolean> {
  const data = await arcAuthClient.request<{ deleteUser: boolean }>(
    `mutation DeleteUser($username: String!) { deleteUser(username: $username) }`,
    { username }
  );
  return data.deleteUser;
}

export async function reAuthenticate(username: string, password: string): Promise<boolean> {
  const res = await fetch(arcAuthUrl("login"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userID: username, password }),
  });
  return res.ok;
}

export async function arcLogin(username: string, password: string): Promise<string | null> {
  const res = await fetch(arcAuthUrl("login"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userID: username, password }),
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`Login failed: ${res.status}`);
  const data = await res.json() as { userToken: string; refreshToken?: string };
  if (data.refreshToken) TokenStore.setRefreshToken(data.refreshToken);
  return data.userToken;
}

export async function validateSession(): Promise<boolean> {
  const token = getAuthToken();
  if (!token) return false;
  const res = await fetch(arcAuthUrl("validate"), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.ok) return true;
  if (res.status === 401) return false;
  throw new Error(`/validate returned ${res.status}`);
}

export async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = TokenStore.getRefreshToken();
  if (!refreshToken) return false;
  const res = await fetch(arcAuthUrl("refresh"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) return false;
  const data = await res.json() as { userToken: string; refreshToken: string };
  TokenStore.setAccessToken(data.userToken);
  TokenStore.setRefreshToken(data.refreshToken);
  return true;
}

export async function arcLogout(): Promise<void> {
  const refreshToken = TokenStore.getRefreshToken();
  if (refreshToken) {
    try {
      await fetch(arcAuthUrl("logout"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
    } catch {
      // best effort — ignore network errors
    }
  }
  TokenStore.clear();
}

export function getLoggedInUsername(): string | null {
  const token = getAuthToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1])) as { sub?: string };
    return payload.sub ?? null;
  } catch {
    return null;
  }
}
